import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon, X } from 'lucide-react';
import type { MediaAdminItem } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { mediaAbsoluteUrl, mediaListQueryOptions } from '@/lib/media';

interface CoverImagePickerProps {
  /** Currently selected cover (null = none). */
  selected: { id: string; url: string; altText: string | null } | null;
  onChange: (next: { id: string; url: string; altText: string | null } | null) => void;
}

/**
 * Inline cover image control: shows current thumbnail + actions to open a
 * media-library picker or clear the selection. Picker grid pulls from the
 * same admin media endpoint as /media.
 */
export function CoverImagePicker({ selected, onChange }: CoverImagePickerProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div className="space-y-2">
      {selected ? (
        <div className="relative overflow-hidden rounded-md border border-slate-200 dark:border-slate-800">
          <img
            src={mediaAbsoluteUrl(selected.url)}
            alt={selected.altText ?? 'Cover'}
            className="aspect-video w-full object-cover"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="ลบรูปปก"
            className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500 hover:border-slate-400 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
        >
          <ImageIcon className="mr-2 h-4 w-4" />
          เลือกรูปปก
        </button>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setPickerOpen(true)}
          className="flex-1"
        >
          {selected ? 'เปลี่ยนรูป' : 'เลือกจาก Media Library'}
        </Button>
      </div>

      <p className="text-[11px] text-slate-500">
        ใช้เป็นรูปแทนใน list + og:image สำหรับการแชร์ social
      </p>

      <PickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={(item) => {
          onChange({ id: item.id, url: item.url, altText: item.altText });
          setPickerOpen(false);
        }}
      />
    </div>
  );
}

interface PickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (item: MediaAdminItem) => void;
}

function PickerDialog({ open, onOpenChange, onPick }: PickerDialogProps) {
  const [search, setSearch] = useState('');
  const query = useQuery(
    mediaListQueryOptions({
      perPage: 24,
      q: search || undefined,
      kind: 'image',
    }),
  );
  const items = query.data?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>เลือกรูปปก</DialogTitle>
          <DialogDescription>
            เลือกจากรูปที่อัปโหลดแล้ว — รูปใหม่ใส่ผ่าน editor บทความหรือ /media
          </DialogDescription>
        </DialogHeader>

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหาตามชื่อไฟล์ / alt text..."
          className="mb-3"
        />

        {query.isLoading ? (
          <p className="py-8 text-center text-sm text-slate-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">ไม่พบรูป</p>
        ) : (
          <div className="grid max-h-[60vh] grid-cols-3 gap-3 overflow-y-auto sm:grid-cols-4 md:grid-cols-6">
            {items.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onPick(m)}
                className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-100 hover:border-slate-400 dark:border-slate-800 dark:bg-slate-800"
              >
                <img
                  src={mediaAbsoluteUrl(m.url)}
                  alt={m.altText ?? m.originalName}
                  loading="lazy"
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
